import * as React from "react";

import styled from "styled-components";
import { Button, Space, Typography } from "antd";
import { Link } from "@opendash/router";
import { useTranslation } from "@opendash/i18n";
import { useConsent } from "./ConsentContext";
import { useAppState } from "../state";

const { Text, Title } = Typography;

const BannerBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10000000000;
  background: white;
  border-top: 4px solid var(--primaryColor);
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.15);
  padding: 20px 24px;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const TextWrap = styled.div`
  flex: 1 1 360px;
  min-width: 260px;
`;

const Buttons = styled(Space)`
  flex-wrap: wrap;

  .ant-btn {
    min-width: 200px;
    height: 44px;
    font-size: 15px;
    font-weight: 500;
  }
`;

const StatusRow = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const PrivacyLink = styled.span`
  a {
    color: var(--primaryColor, #1677ff);
    text-decoration: underline;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #999;
  padding: 4px 8px;

  &:hover {
    color: #333;
  }
`;

export const ConsentBanner = React.memo(function ConsentBanner() {
  const t = useTranslation();
  const state = useAppState();
  const { consent, grant, deny, settingsOpen, closeSettings } = useConsent();

  const visible = consent === null || settingsOpen;
  if (!visible) return null;

  const privacyUrl = state.tenantConfig?.get("privacyUrl");
  const privacyText = state.tenantConfig?.get("privacyText");

  const renderPrivacyLink = () => {
    if (privacyUrl) {
      return (
        <PrivacyLink>
          <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
            {t("app:footer.privacy")}
          </a>
        </PrivacyLink>
      );
    }
    if (privacyText) {
      return (
        <PrivacyLink>
          <Link to="/datenschutz" onClick={closeSettings}>
            {t("app:footer.privacy")}
          </Link>
        </PrivacyLink>
      );
    }
    return null;
  };

  const isInitial = consent === null;
  const currentStatus =
    consent === "granted"
      ? t("app:consent.status_granted")
      : consent === "denied"
        ? t("app:consent.status_denied")
        : null;

  return (
    <BannerBar role="region" aria-labelledby="consent-title">
      {!isInitial && (
        <CloseButton
          aria-label={t("app:consent.close") as string}
          onClick={closeSettings}
        >
          ×
        </CloseButton>
      )}
      <Inner>
        <TextWrap>
          <Title
            id="consent-title"
            level={5}
            style={{ margin: 0, marginBottom: 6 }}
          >
            {t("app:consent.title")}
          </Title>
          <Text style={{ fontSize: 14, lineHeight: 1.5, display: "block" }}>
            {t("app:consent.description")}
          </Text>
          <div style={{ marginTop: 6 }}>{renderPrivacyLink()}</div>
          {!isInitial && currentStatus && (
            <StatusRow>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t("app:consent.current_status")}:{" "}
                <strong>{currentStatus}</strong>
              </Text>
            </StatusRow>
          )}
        </TextWrap>
        <Buttons size="middle">
          <Button onClick={deny} size="large">
            {t("app:consent.deny")}
          </Button>
          <Button onClick={grant} size="large">
            {t("app:consent.accept")}
          </Button>
        </Buttons>
      </Inner>
    </BannerBar>
  );
});
